/**
 * resolveDmlTargetAlias — alias↔relation resolution for UPDATE/DELETE
 * targets whose primary target position is written as a bare alias (mssql
 * `UPDATE p SET … FROM Production.Product p …`, `DELETE p FROM
 * Production.Product p …`) or whose FROM/TABLES tail is nothing more than
 * the target's own alias leaking through because the render-level outline
 * walker doesn't consume it as part of the target (MySQL
 * `UPDATE customer c JOIN payment p ON … SET …`, Oracle
 * `UPDATE orders o SET … (correlated subquery) …`). An alias-only pill/row
 * is information-free — closes DML-14/15/17/19.
 *
 * `resolveDmlTargetAlias` is the pure matcher: it never touches SQL text
 * itself, only a normalized `targetText` + a list of alias/relation
 * candidates gathered from the FROM/TABLES tail (by the text-based helpers
 * below) or from `QueryStructure.fromItems` (by `compact/compactSummary.ts`,
 * which already has structured data and needs no text parsing).
 */

import type { ParseOptions } from '../../../../sql-grammar/model';
import { extractFromSegmentJoins } from '../select/extractFromJoins';
import { splitTopLevelCommas } from '../select/splitTopLevel';

export interface DmlAliasCandidate {
  /**
   * Relation name/qualified text for this FROM/TABLES-tail item, or `null`
   * when the item is a bare token with no relation of its own — the
   * MySQL/Oracle shape where the tail is nothing but the target's own alias.
   */
  readonly relation: string | null;
  /** Alias bound to this item, or `null` for a bare relation reference. */
  readonly alias: string | null;
}

export interface ResolvedDmlTarget {
  /** Alias exactly as written, or `null` when nothing resolves as an alias. */
  readonly alias: string | null;
  /** Resolved relation name, or `null` when an alias is known but unresolved. */
  readonly relation: string | null;
}

/**
 * Resolve `targetText` (the DML target exactly as parsed — e.g. `p` /
 * `customer` / `orders`) against `candidates` gathered from the statement's
 * FROM/TABLES tail.
 *
 * - A candidate whose `alias` equals `targetText` names `targetText`'s real
 *   relation (mssql/DELETE `UPDATE p … FROM Production.Product p`) →
 *   `{ alias: targetText, relation: candidate.relation }`. When that
 *   candidate's relation is itself unresolvable (or a self-reference), the
 *   alias is surfaced with `relation: null` — present, but unresolved.
 * - Otherwise, a bare-token candidate (no relation of its own) is read as
 *   the target's *own* alias, leaked into the tail (MySQL
 *   `customer c [JOIN …]`, Oracle `orders o`) →
 *   `{ alias: candidate.alias, relation: targetText }`.
 * - Otherwise `targetText` is the plain relation, no alias →
 *   `{ alias: null, relation: targetText }`.
 */
export function resolveDmlTargetAlias(
  targetText: string,
  candidates: readonly DmlAliasCandidate[],
): ResolvedDmlTarget {
  const target = targetText.trim();
  if (!target) return { alias: null, relation: null };
  const targetLower = target.toLowerCase();

  const aliasMatch = candidates.find(
    (c) => c.alias !== null && c.alias.trim().toLowerCase() === targetLower,
  );
  if (aliasMatch) {
    const relation =
      aliasMatch.relation !== null && aliasMatch.relation.trim().toLowerCase() !== targetLower
        ? aliasMatch.relation
        : null;
    return { alias: target, relation };
  }

  const bareAlias = candidates.find((c) => c.relation === null && c.alias !== null);
  if (bareAlias) {
    return { alias: bareAlias.alias, relation: target };
  }

  return { alias: null, relation: target };
}

/** `relation (alias)` when the alias adds information, else just `relation`. */
export function formatRelationAlias(relation: string, alias: string | null): string {
  return alias && alias.trim().toLowerCase() !== relation.trim().toLowerCase()
    ? `${relation} (${alias})`
    : relation;
}

/**
 * Full-target-pill formatter shared by `UpdateRenderer`/`DeleteRenderer`.
 * Two resolved shapes read differently: when `targetText` itself *was* the
 * alias, the newly-discovered relation reads as `alias → relation`; when
 * `targetText` was already the relation and the tail only contributed an
 * alias, it reads as `relation (alias)` (`formatRelationAlias`). Falls back
 * to the raw `targetText` when nothing resolved.
 */
export function formatDmlTargetText(targetText: string, resolved: ResolvedDmlTarget): string {
  const trimmed = targetText.trim();
  if (!resolved.relation) return trimmed;
  if (resolved.alias && resolved.alias.trim().toLowerCase() === trimmed.toLowerCase()) {
    return `${trimmed} → ${resolved.relation}`;
  }
  return formatRelationAlias(resolved.relation, resolved.alias);
}

/**
 * Text-based candidate builder for a FROM/USING-like tail (comma-separated
 * relation refs, each optionally followed by JOIN strips) — one candidate
 * per segment's leading item plus one per JOIN's right-hand side. Used by
 * Update/DeleteRenderer's alias-as-target resolution (mssql/DELETE
 * `UPDATE p … FROM Production.Product p …`) when only raw SQL text is
 * available (no `QueryStructure` snapshot).
 */
export function buildAliasCandidatesFromText(
  bufferText: string,
  start: number,
  end: number,
  options: ParseOptions,
): DmlAliasCandidate[] {
  const out: DmlAliasCandidate[] = [];
  for (const seg of splitTopLevelCommas(bufferText, start, end, options)) {
    const split = extractFromSegmentJoins(bufferText, seg.start, seg.end, options);
    if (split.leading.text) out.push(splitRelationAlias(split.leading.text));
    for (const join of split.joins) {
      if (join.rhs.text) out.push(splitRelationAlias(join.rhs.text));
    }
  }
  return out;
}

export interface BareAliasTail {
  /** Non-null when the tail's leading token is a plausible bare alias. */
  readonly candidate: DmlAliasCandidate | null;
  /** True when the tail has real content beyond that leading alias (a JOIN). */
  readonly hasJoins: boolean;
}

/**
 * Detects the MySQL/Oracle "leaked trailing alias" shape: a multi-table tail
 * that directly follows the target with no comma (ruling out a genuine
 * second comma-listed table) and whose leading segment, up to the first
 * JOIN keyword, is a single bare identifier. That identifier is the
 * target's *own* alias, not a second table (MySQL
 * `customer c [JOIN payment p ON …]`, Oracle `orders o`).
 */
export function readBareAliasTail(
  bufferText: string,
  start: number,
  end: number,
  options: ParseOptions,
): BareAliasTail {
  const raw = bufferText.slice(start, end);
  if (/^\s*,/.test(raw)) return { candidate: null, hasJoins: true };

  const split = extractFromSegmentJoins(bufferText, start, end, options);
  const leading = split.leading.text;
  const hasJoins = split.joins.length > 0;
  if (!leading || /[\s.]/.test(leading)) return { candidate: null, hasJoins };
  return { candidate: { relation: null, alias: leading }, hasJoins };
}

function splitRelationAlias(text: string): DmlAliasCandidate {
  const trimmed = text.trim();
  if (!trimmed) return { relation: null, alias: null };

  const asMatch = /^(.*\S)\s+AS\s+(\S+)$/i.exec(trimmed);
  if (asMatch) {
    return { relation: asMatch[1].trim() || null, alias: asMatch[2].trim() || null };
  }

  const bareMatch = /^(.*\S)\s+(\S+)$/.exec(trimmed);
  if (bareMatch) {
    return { relation: bareMatch[1].trim() || null, alias: bareMatch[2].trim() || null };
  }

  return { relation: trimmed, alias: null };
}
